import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  CurrencyDollarIcon,
  CalendarIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';
import loanService from '../../services/loanService';
import clientService from '../../services/clientService';
import { useToast } from '../../contexts/ToastContext';

const LoanForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [loanData, setLoanData] = useState(null);
  const [clients, setClients] = useState([]);
  const [loanProducts, setLoanProducts] = useState([]);
  const [calculatedSchedule, setCalculatedSchedule] = useState([]);

  useEffect(() => {
    fetchInitialData();
    if (isEditMode) {
      fetchLoan();
    }
  }, [id]);

  const fetchInitialData = async () => {
    try {
      const [clientsData, productsData] = await Promise.all([
        clientService.getAllClients(),
        fetchLoanProducts()
      ]);
      setClients(clientsData);
      setLoanProducts(productsData);
    } catch (error) {
      showError('Erreur lors du chargement des données');
    }
  };

  const fetchLoanProducts = async () => {
    // Mock data - in real app, fetch from API
    return [
      { id: '1', name: 'Prêt Agricole', code: 'AGR', interest_rate: 12, min_amount: 50000, max_amount: 500000, term_min: 6, term_max: 24 },
      { id: '2', name: 'Prêt Commercial', code: 'COM', interest_rate: 15, min_amount: 100000, max_amount: 1000000, term_min: 3, term_max: 36 },
      { id: '3', name: 'Prêt Éducation', code: 'EDU', interest_rate: 10, min_amount: 20000, max_amount: 300000, term_min: 6, term_max: 48 },
    ];
  };

  const fetchLoan = async () => {
    try {
      setLoading(true);
      const data = await loanService.getLoan(id);
      setLoanData(data);
      Object.keys(data).forEach(key => {
        if (formik.values[key] !== undefined) {
          formik.setFieldValue(key, data[key]);
        }
      });
    } catch (error) {
      showError('Erreur lors du chargement du prêt');
      navigate('/loans');
    } finally {
      setLoading(false);
    }
  };

  const calculateLoanSchedule = (values) => {
    if (!values.amount || !values.interest_rate || !values.term) {
      return [];
    }

    const principal = parseFloat(values.amount);
    const monthlyRate = (parseFloat(values.interest_rate) / 100) / 12;
    const term = parseInt(values.term);

    // Calculate monthly payment
    const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1);

    const schedule = [];
    let balance = principal;

    for (let i = 1; i <= term; i++) {
      const interest = balance * monthlyRate;
      const principalPayment = monthlyPayment - interest;
      balance -= principalPayment;

      schedule.push({
        installment: i,
        principal: principalPayment,
        interest: interest,
        total: monthlyPayment,
        balance: balance > 0 ? balance : 0,
      });
    }

    return schedule;
  };

  const validationSchema = Yup.object({
    client_id: Yup.string().required('Le client est requis'),
    loan_product_id: Yup.string().required('Le produit de prêt est requis'),
    amount: Yup.number()
      .required('Le montant est requis')
      .min(1000, 'Le montant minimum est 1,000 HTG')
      .max(10000000, 'Le montant maximum est 10,000,000 HTG'),
    interest_rate: Yup.number()
      .required('Le taux d\'intérêt est requis')
      .min(0.1, 'Le taux doit être supérieur à 0.1%')
      .max(100, 'Le taux maximum est 100%'),
    term: Yup.number()
      .required('La durée est requise')
      .min(1, 'La durée minimum est 1 mois')
      .max(120, 'La durée maximum est 120 mois'),
    start_date: Yup.date().required('La date de début est requise'),
    purpose: Yup.string().required('L\'objet du prêt est requis'),
  });

  const formik = useFormik({
    initialValues: {
      client_id: '',
      loan_product_id: '',
      amount: '',
      interest_rate: '',
      term: '',
      start_date: new Date().toISOString().split('T')[0],
      purpose: '',
      payment_frequency: 'monthly',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        
        const formattedValues = {
          ...values,
          amount: parseFloat(values.amount),
          interest_rate: parseFloat(values.interest_rate),
          term: parseInt(values.term),
        };

        if (isEditMode) {
          await loanService.updateLoan(id, formattedValues);
          showSuccess('Prêt mis à jour avec succès');
        } else {
          await loanService.createLoan(formattedValues);
          showSuccess('Prêt créé avec succès');
        }
        
        navigate('/loans');
      } catch (error) {
        showError(error.response?.data?.message || 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    },
  });

  const handleProductChange = (productId) => {
    const product = loanProducts.find(p => p.id === productId);
    if (product) {
      formik.setFieldValue('loan_product_id', product.id);
      formik.setFieldValue('interest_rate', product.interest_rate);
      formik.setFieldValue('term', product.term_min || 12);
    }
  };

  const handleCalculate = () => {
    if (formik.values.amount && formik.values.interest_rate && formik.values.term) {
      const schedule = calculateLoanSchedule(formik.values);
      setCalculatedSchedule(schedule);
    }
  };

  const totalInterest = calculatedSchedule.reduce((sum, payment) => sum + payment.interest, 0);
  const totalAmount = parseFloat(formik.values.amount || 0) + totalInterest;
  const monthlyPayment = calculatedSchedule[0]?.total || 0;

  if (loading && isEditMode && !loanData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEditMode ? 'Modifier le Prêt' : 'Nouveau Prêt'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isEditMode
              ? 'Modifiez les détails du prêt'
              : 'Créez un nouveau prêt pour un client'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Section */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="p-6">
              <form onSubmit={formik.handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Client */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Client *
                    </label>
                    <select
                      name="client_id"
                      value={formik.values.client_id}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="input-field"
                    >
                      <option value="">Sélectionnez un client</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.client_number} - {client.first_name} {client.last_name}
                        </option>
                      ))}
                    </select>
                    {formik.touched.client_id && formik.errors.client_id && (
                      <p className="mt-2 text-sm text-red-600">{formik.errors.client_id}</p>
                    )}
                  </div>

                  {/* Loan Product */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Produit de prêt *
                    </label>
                    <select
                      name="loan_product_id"
                      value={formik.values.loan_product_id}
                      onChange={(e) => handleProductChange(e.target.value)}
                      onBlur={formik.handleBlur}
                      className="input-field"
                    >
                      <option value="">Sélectionnez un produit</option>
                      {loanProducts.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} ({product.interest_rate}%)
                        </option>
                      ))}
                    </select>
                    {formik.touched.loan_product_id && formik.errors.loan_product_id && (
                      <p className="mt-2 text-sm text-red-600">{formik.errors.loan_product_id}</p>
                    )}
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Montant (HTG) *
                    </label>
                    <input
                      type="number"
                      name="amount"
                      value={formik.values.amount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="input-field"
                      placeholder="0"
                    />
                    {formik.touched.amount && formik.errors.amount && (
                      <p className="mt-2 text-sm text-red-600">{formik.errors.amount}</p>
                    )}
                  </div>

                  {/* Interest Rate */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Taux d'intérêt (%) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      name="interest_rate"
                      value={formik.values.interest_rate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="input-field"
                      placeholder="0.0"
                    />
                    {formik.touched.interest_rate && formik.errors.interest_rate && (
                      <p className="mt-2 text-sm text-red-600">{formik.errors.interest_rate}</p>
                    )}
                  </div>

                  {/* Term */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Durée (mois) *
                    </label>
                    <input
                      type="number"
                      name="term"
                      value={formik.values.term}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="input-field"
                      placeholder="12"
                    />
                    {formik.touched.term && formik.errors.term && (
                      <p className="mt-2 text-sm text-red-600">{formik.errors.term}</p>
                    )}
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Date de début *
                    </label>
                    <input
                      type="date"
                      name="start_date"
                      value={formik.values.start_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="input-field"
                    />
                    {formik.touched.start_date && formik.errors.start_date && (
                      <p className="mt-2 text-sm text-red-600">{formik.errors.start_date}</p>
                    )}
                  </div>

                  {/* Payment Frequency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Fréquence de paiement
                    </label>
                    <select
                      name="payment_frequency"
                      value={formik.values.payment_frequency}
                      onChange={formik.handleChange}
                      className="input-field"
                    >
                      <option value="weekly">Hebdomadaire</option>
                      <option value="biweekly">Bimensuel</option>
                      <option value="monthly">Mensuel</option>
                      <option value="quarterly">Trimestriel</option>
                    </select>
                  </div>
                </div>

                {/* Purpose */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Objet du prêt *
                  </label>
                  <textarea
                    name="purpose"
                    rows={3}
                    value={formik.values.purpose}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="input-field"
                    placeholder="Décrivez l'utilisation prévue du prêt"
                  />
                  {formik.touched.purpose && formik.errors.purpose && (
                    <p className="mt-2 text-sm text-red-600">{formik.errors.purpose}</p>
                  )}
                </div>

                {/* Calculate Button */}
                <div>
                  <button
                    type="button"
                    onClick={handleCalculate}
                    className="btn-secondary"
                  >
                    <CalculatorIcon className="h-5 w-5 mr-2" />
                    Calculer l'échéancier
                  </button>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate('/loans')}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading || !formik.isValid}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isEditMode ? 'Mise à jour...' : 'Création...'}
                      </>
                    ) : (
                      isEditMode ? 'Mettre à jour' : 'Créer le prêt'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="space-y-6">
            {/* Loan Summary */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Récapitulatif</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Montant principal</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formik.values.amount ? parseFloat(formik.values.amount).toLocaleString('fr-FR') : '0'} HTG
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Taux d'intérêt</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formik.values.interest_rate || '0'}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Durée</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formik.values.term || '0'} mois
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Intérêts totaux</span>
                    <span className="text-sm font-medium text-gray-900">
                      {totalInterest.toLocaleString('fr-FR')} HTG
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Montant total</span>
                    <span className="text-sm font-medium text-gray-900">
                      {totalAmount.toLocaleString('fr-FR')} HTG
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-200">
                    <span className="text-sm font-medium text-gray-900">Paiement mensuel</span>
                    <span className="text-sm font-bold text-gray-900">
                      {monthlyPayment.toLocaleString('fr-FR')} HTG
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Schedule Preview */}
            {calculatedSchedule.length > 0 && (
              <div className="card">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Échéancier (3 premiers mois)</h3>
                  <div className="space-y-2">
                    {calculatedSchedule.slice(0, 3).map((payment) => (
                      <div key={payment.installment} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <span className="text-sm font-medium text-gray-900">Mois {payment.installment}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {payment.total.toLocaleString('fr-FR')} HTG
                          </div>
                          <div className="text-xs text-gray-500">
                            Principal: {payment.principal.toLocaleString('fr-FR')} | Intérêt: {payment.interest.toLocaleString('fr-FR')}
                          </div>
                        </div>
                      </div>
                    ))}
                    {calculatedSchedule.length > 3 && (
                      <div className="text-center pt-2">
                        <span className="text-sm text-gray-500">
                          + {calculatedSchedule.length - 3} autres échéances
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Requirements */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Documents requis</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 text-green-500">✓</div>
                    <span className="ml-2">Pièce d'identité du client</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 text-green-500">✓</div>
                    <span className="ml-2">Justificatif de domicile</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 text-yellow-500">!</div>
                    <span className="ml-2">Garanties (si montant &gt; 500,000 HTG)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanForm;